import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { MenuItemKey } from '../../components/Menu'
import { Pivot, PivotItem } from '@fluentui/react'
import { Route, Switch } from 'react-router'
import InvoiceOverview from './Invoice/InvoiceOverview'
import Reports from './Reports'
import { AppAction } from '../../config/appConfig'
import EditInvoice from './Invoice/EditInvoice'
import AddInvoice from './Invoice/AddInvoice'
import { useHistory, useLocation } from 'react-router-dom'
import { ModuleType } from '../../context/GlobalContext'
import useModules from '../../hooks/module/useModules'
import useRoles, { Role } from '../../hooks/useRoles'
import { useTranslation } from 'react-i18next'

type PivotItemProps = {
    key: string
    title: string
    requiredAuthorizationRoles?: Role[]
    requiredModule?: ModuleType
}

const getItemRoute = (items: PivotItemProps[], key?: string): string => {
    // By default, item's key will be equal to the route, but it may be overridden by the route property.
    const item: PivotItemProps = items.find((item: PivotItemProps) => item.key === key)
    return `/finance/${item ? item.key : ''}`
}

export default function Dashboard(props): ReactElement {
    const roles = useRoles()
    const modules = useModules()
    const { t: translate } = useTranslation()

    const allowedPivotItems: PivotItemProps[] = useMemo<PivotItemProps[]>(
        () =>
            [
                {
                    key: 'invoices',
                    title: translate('finance.menu.invoice'),
                    requiredAuthorizationRoles: [Role.Invoice],
                    requiredModule: ModuleType.Invoices,
                },
                {
                    key: 'reports',
                    title: translate('finance.menu.reports'),
                    requiredAuthorizationRoles: [Role.Report],
                    requiredModule: ModuleType.Reports,
                },
            ].filter(item => {
                const module = !item.requiredModule || modules.has(item.requiredModule)
                const role = !item.requiredAuthorizationRoles || roles.all(item.requiredAuthorizationRoles)

                return module && role
            }),
        [],
    )

    const [activeTab, setActiveTab] = useState<string>('')
    const dispatch = useDispatch()
    const history = useHistory()
    const { pathname } = useLocation()

    useEffect(() => {
        const currentLink = allowedPivotItems.find(
            item => pathname.search(getItemRoute(allowedPivotItems, item.key)) === 0,
        )
        if (!currentLink && allowedPivotItems[0]) {
            history.push(getItemRoute(allowedPivotItems, allowedPivotItems[0].key))
        }
    }, [])

    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Finance] })
    }, [props])

    useEffect(() => {
        const currentLink = allowedPivotItems.find(
            item => pathname.search(getItemRoute(allowedPivotItems, item.key)) === 0,
        )
        if (currentLink) {
            setActiveTab(currentLink.key)
        }
    }, [pathname])

    return (
        <>
            <Pivot
                linkSize="normal"
                linkFormat="tabs"
                selectedKey={activeTab}
                onLinkClick={item => history.push(getItemRoute(allowedPivotItems, item.props.itemKey))}
                styles={{ root: { marginBottom: 8 } }}
            >
                {allowedPivotItems.map(item => (
                    <PivotItem key={item.key} itemKey={item.key} headerText={item.title} />
                ))}
            </Pivot>
            {/*The Switch can't handle 2 IFs with empty nesting <> </> ...*/}
            <Switch>
                {modules.has(ModuleType.Invoices) && <Route exact path="/finance" component={InvoiceOverview} />}
                {modules.has(ModuleType.Invoices) && (
                    <Route exact path="/finance/invoices" component={InvoiceOverview} />
                )}
                {modules.has(ModuleType.Invoices) && (
                    <Route exact path="/finance/invoices/add" component={AddInvoice} />
                )}
                {modules.has(ModuleType.Invoices) && (
                    <Route path="/finance/invoices/:id/edit" component={EditInvoice} />
                )}
                {modules.has(ModuleType.Reports) && <Route path="/finance/reports" component={Reports} />}
            </Switch>
        </>
    )
}
